import { Box, Button, Flex, Text } from '@chakra-ui/react';
import Balance from '@ui/components/Balance';
import Info from '@ui/components/Info';
import {
  ModalOrDrawer,
  ModalOrDrawerBody,
  ModalOrDrawerCloseButton,
  ModalOrDrawerContent,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer';
import { StrategyApySimpleLineChart } from '@ui/components/StrategyApySimpleLineChart';
import { PATH_DASHBOARD, pathDict } from '@ui/config/paths';
import { useHistoricStrategyApys } from '@ui/hooks/useApr';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useStrategyTokenUsdValue } from '@ui/hooks/useStrategyTokenUsdValue';
import { useTranslations } from '@ui/i18n';
import { useRouter } from '@ui/overrides/next/router';
import {
  formatNumber,
  formatPercentage,
  formatTokenAmount,
} from '@ui/utils/format';
import {
  LiquidityStrategyTokenDict,
  useOwnedDepositCoinAmount,
  useStrategyApy,
  useVaultBalance,
} from '@ui/utils/strategy';
import { pushRouterWithoutRerender } from '@ui/utils/urlHistory';
import { resetWhenRouterChange } from '@ui/utils/zustandMiddleware';
import React from 'react';
import { create } from 'zustand';

function StrategyDashboardDrawer() {
  const { isOpen, toggle } = useStrategyDashboardDrawerStore();
  const apy = useStrategyApy();
  const t = useTranslations();
  const router = useRouter();
  const tokenInfo = LiquidityStrategyTokenDict['wcro'];
  const ownedDepositWcro = useOwnedDepositCoinAmount();
  const { data: ownedDepositUsd } = useCoinUsdValue(ownedDepositWcro);
  const ownedBalance = useVaultBalance().data;
  const ownedBalanceUsd = useStrategyTokenUsdValue(ownedBalance);
  const apyList = useHistoricStrategyApys();
  const goToStrategy = () => {
    toggle(false);
    router.push(pathDict.cro.strategy.deposit);
  };

  return (
    <ModalOrDrawer
      variant={{ base: 'backward', desktop: 'default' }}
      placement="right"
      size={{
        base: 'full',
        desktop: 'md',
      }}
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={() => toggle(false)}
    >
      <ModalOrDrawerOverlay />
      <ModalOrDrawerContent>
        <ModalOrDrawerCloseButton />
        <ModalOrDrawerHeader>{t('Liquidity Strategy')}</ModalOrDrawerHeader>
        <ModalOrDrawerBody pb="40px" gap={4} display="flex" flexDir="column">
          <Balance
            title={`Your value`}
            icon={<tokenInfo.Icon />}
            amount={ownedDepositWcro}
            usdAmount={ownedDepositUsd}
            usdDisplayDecimals={2}
            showUsdAmount
          />
          <Flex flexDir="column">
            <Text>{t('Strategy performance')}</Text>
            {apyList && <StrategyApySimpleLineChart apyList={apyList} mt={4} />}
          </Flex>
          <Flex flexDir="column" gap={2}>
            <Info
              title={t('Estimated APY')}
              value={
                apy.isLoading
                  ? undefined
                  : formatPercentage(apy.data.strategyApy)
              }
            />
            <Info
              title={t('Immediate APY')}
              value={
                apy.isLoading
                  ? undefined
                  : formatPercentage(apy.data.immediateApy)
              }
            />
            <Text color="text-light">{t('strategy-apy-explainer')}</Text>
            {(ownedBalance === undefined || ownedBalance.gt(0)) && (
              <>
                <Info
                  title={t('{coin} balance', {
                    coin: tokenInfo.vaultTokenName,
                  })}
                  value={
                    ownedBalance ? formatTokenAmount(ownedBalance) : undefined
                  }
                  convertedValue={
                    ownedBalanceUsd !== null
                      ? formatNumber(ownedBalanceUsd ?? 0, 2) + ' USD'
                      : undefined
                  }
                />
                <Box w="full" textAlign="right">
                  <Button size="sm" onClick={goToStrategy}>
                    {t('Deposit more')}
                  </Button>
                </Box>
              </>
            )}
            {ownedBalance?.lte(0) && (
              <Button onClick={goToStrategy} mt={6}>
                {t('Deposit in {platform}', {
                  platform: t('Liquidity strategy'),
                })}
              </Button>
            )}
          </Flex>
        </ModalOrDrawerBody>
      </ModalOrDrawerContent>
    </ModalOrDrawer>
  );
}

export default StrategyDashboardDrawer;

export type StrategyDashboardDrawerStore = {
  isOpen: boolean;
  toggle: (isOpen?: boolean) => void;
};

export const useStrategyDashboardDrawerStore =
  create<StrategyDashboardDrawerStore>(
    resetWhenRouterChange((set) => ({
      isOpen: false,
      toggle: (isOpen) => {
        pushRouterWithoutRerender(PATH_DASHBOARD);
        set((state) => {
          return {
            isOpen: typeof isOpen !== 'undefined' ? isOpen : !state.isOpen,
          };
        });
      },
    })),
  );
